/* ==========================================================================
   Luxuria theme — theme.js
   • Motion: GSAP + ScrollTrigger + SplitText + Flip + CustomEase, Lenis smooth scroll
   • Commerce: cart drawer, predictive search, filters, product variants
     (Section Rendering API), lightbox, announcement bar, account helpers
   Every module checks that its markup exists, so sections can be added,
   removed and reordered freely in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  var LUX = window.LUX || {};
  var doc = document;
  var root = doc.documentElement;
  var reduced = !!LUX.reducedMotion;
  var animate = LUX.animations !== false && !reduced;
  var isMobile = function () { return window.innerWidth < 768; };
  var S = LUX.strings || {};
  var R = LUX.routes || {};

  var $ = function (s, c) { return (c || doc).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || doc).querySelectorAll(s)); };
  var debounce = function (fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; };

  if (!animate) { root.classList.add('lux-static'); }
  var hasGSAP = function () { return !!(window.gsap && window.ScrollTrigger); };

  /* ---------- shared state ---------- */
  var lenis = null;
  var textRecs = [];
  var heroLines = [];
  var cleanups = [];
  var mm = [];
  var booted = false;
  var preloaderRan = false;

  /* =======================================================================
     Smooth scrolling (Lenis) — desktop only
     ======================================================================= */
  function initSmoothScroll() {
    if (lenis || !LUX.smoothScroll || reduced || isMobile() || !window.Lenis || !hasGSAP()) return;
    lenis = new window.Lenis({ smooth: true, lerp: 0.1, wheelMultiplier: 1, smoothTouch: false });
    lenis.on('scroll', window.ScrollTrigger.update);
    window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    window.gsap.ticker.lagSmoothing(0);
    window.__LENIS__ = lenis;
    if (LUX.preloader && !preloaderRan) lenis.stop();
  }
  function lockScroll(lock) {
    doc.body.classList.toggle('scroll-locked', lock);
    doc.body.style.overflow = lock ? 'hidden' : '';
    if (lenis) { lock ? lenis.stop() : lenis.start(); }
  }
  function scrollToEl(target) {
    if (lenis) lenis.scrollTo(target, { duration: 1.4, easing: function (x) { return 1 - Math.pow(1 - x, 4); } });
    else target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
  }

  /* =======================================================================
     Preloader (home, first visit per session)
     ======================================================================= */
  function initPreloader(done) {
    var pre = $('.rx-pre');
    var hero = $('.hero');
    var media = $('.hero .video-container');
    var heroP = $('.hero-anim-p');

    if (!LUX.preloader || !pre || !hasGSAP()) {
      root.classList.remove('lux-preloader');
      done();
      return;
    }
    var gsap = window.gsap;
    var ease = window.CustomEase ? window.CustomEase.create('rxEase', '.76,0,.24,1') : 'power3.inOut';
    var fill = $('.rx-pre__fill', pre), band = $('.rx-pre__band', pre);
    var halfA = $('.rx-pre__half--a', pre), halfB = $('.rx-pre__half--b', pre);
    var logo = $('.rx-pre__logo', pre), count = $('.rx-pre__count', pre), tag = $('.rx-pre__tag', pre);
    var counter = $('#rx-count');
    var words = $$('.rx-pre__tag em', pre);
    var SIN = Math.sin(35 * Math.PI / 180), COS = Math.cos(35 * Math.PI / 180);
    var finished = false;

    // geometry along the diagonal's perpendicular axis (the stage is rotated -35deg)
    function geo() {
      var W = window.innerWidth, H = window.innerHeight;
      var bandH = Math.round(Math.max(W, H) * 0.075);
      pre.style.setProperty('--rx-band', bandH + 'px');
      return { bandH: bandH, half: (W * SIN + H * COS) / 2 };
    }
    function paint(v) {
      var g = geo();
      fill.style.clipPath = 'inset(0 ' + (100 - v) + '% 0 0)';
      gsap.set(band, { y: -(g.half + g.bandH + 40) * (1 - v / 100) });
      counter.textContent = Math.round(v);
    }

    if (media) gsap.set(media, { scale: 1.2 });
    if (heroP) gsap.set(heroP, { opacity: 0, y: 30 });
    paint(0);
    if (words.length) gsap.to(words, { y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.09, delay: 0.25 });
    gsap.from(count, { opacity: 0, y: 24, duration: 0.7, ease: 'power3.out', delay: 0.1 });

    var p = { v: 0 };
    var crawl = gsap.to(p, { v: 72, duration: 2.2, ease: 'power2.out', onUpdate: function () { paint(p.v); } });

    function cleanup() {
      if (finished) return;
      finished = true;
      root.classList.remove('lux-preloader');
      doc.body.classList.add('preloader-done');
      if (media) gsap.set(media, { clearProps: 'transform' });
      if (lenis) lenis.start();
      if (heroP) gsap.to(heroP, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' });
      gsap.to('#nav', { opacity: 1, duration: 0.6 });
      setTimeout(function () { window.ScrollTrigger.refresh(); }, 30);
    }

    var minWait = new Promise(function (r) { setTimeout(r, 1800); });
    var loaded = new Promise(function (r) { if (doc.readyState === 'complete') r(); else window.addEventListener('load', r); });
    var fonts = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();

    // never trap the visitor behind the loading screen
    setTimeout(function () { if (!finished) { crawl.kill(); preloaderRan = true; cleanup(); done(); } }, 9000);

    Promise.all([minWait, loaded, fonts]).then(function () {
      if (finished) return;
      preloaderRan = true;
      try { sessionStorage.setItem('lux_preloaded', '1'); } catch (e) {}
      crawl.kill();
      var g = geo();
      var tl = gsap.timeline();
      // 1. finish the fill, stripe docks onto the seam
      tl.to(p, { v: 100, duration: 0.7, ease: 'power2.inOut', onUpdate: function () { paint(p.v); } });
      tl.to(logo, { scale: 1.05, duration: 0.2, ease: 'power2.out', yoyo: true, repeat: 1 }, '>-0.1');
      tl.add(function () { pre.classList.add('is-docked'); });
      tl.to([count, tag], { opacity: 0, duration: 0.25, ease: 'power1.out' }, '<');
      // 2. the screen splits open along the stripe
      tl.addLabel('split', '>+0.05');
      tl.to(halfA, { y: -(g.half + 40), duration: 1.05, ease: ease }, 'split');
      tl.to(halfB, { y: g.half + g.bandH + 40, duration: 1.05, ease: ease }, 'split');
      tl.to(logo, { scale: 1.7, opacity: 0, duration: 0.75, ease: 'power2.in' }, 'split');
      if (media) tl.to(media, { scale: 1, duration: 1.6, ease: 'power3.out' }, 'split');
      tl.add(function () { done(); }, 'split+=0.55');
      tl.add(cleanup, 'split+=1.1');
    });
  }

  /* =======================================================================
     Line reveals (.text-animate) — re-split on width change
     ======================================================================= */
  function buildLines(el) {
    var split = new window.SplitText(el, { type: 'lines', linesClass: 'line' });
    split.lines.forEach(function (line) {
      var inner = doc.createElement('span');
      inner.className = 'line-inner';
      inner.style.display = 'block';
      inner.innerHTML = line.innerHTML;
      line.innerHTML = '';
      line.appendChild(inner);
    });
    return split;
  }
  function initTextAnimations() {
    if (!hasGSAP() || !window.SplitText || !animate) return;
    $$('.text-animate-group p').forEach(function (p) { p.classList.add('text-animate'); });
    $$('.text-animate').forEach(function (el) {
      if (el.__luxSplit) return;
      var rec = { el: el, split: buildLines(el), played: false };
      el.__luxSplit = rec;
      textRecs.push(rec);
      var inners = function () { return el.querySelectorAll('.line-inner'); };
      window.gsap.set(inners(), { y: '100%' });
      var play = function () {
        rec.played = true;
        window.gsap.to(inners(), { y: '0%', duration: 0.8, ease: 'power3.out', stagger: 0.08 });
      };
      if (el.classList.contains('hero-anim') && LUX.preloader && !preloaderRan) { heroLines.push(play); return; }
      window.ScrollTrigger.create({ trigger: el, start: 'top 85%', once: true, onEnter: play });
    });
  }
  var lastW = window.innerWidth, resizeT;
  window.addEventListener('resize', function () {
    clearTimeout(resizeT);
    resizeT = setTimeout(function () {
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      textRecs.forEach(function (r) {
        if (!r.el.isConnected) return;
        r.split.revert();
        r.split = buildLines(r.el);
        window.gsap.set(r.el.querySelectorAll('.line-inner'), { y: r.played ? '0%' : '100%' });
      });
      if (hasGSAP()) window.ScrollTrigger.refresh();
    }, 250);
  });

  /* =======================================================================
     Fade-up on scroll
     ======================================================================= */
  function initFadeUps() {
    if (!hasGSAP() || !animate) return;
    $$('.fade-up').forEach(function (el) {
      if (el.__luxFade) return;
      el.__luxFade = true;
      window.gsap.set(el, { opacity: 0, y: 100 });
      window.ScrollTrigger.create({
        trigger: el, start: 'top bottom', once: true,
        onEnter: function () { window.gsap.to(el, { opacity: 1, y: 0, duration: 1, ease: 'quart.out' }); }
      });
    });
  }

  /* =======================================================================
     Pinned showcase (bezier-arc thumbnails)
     ======================================================================= */
  function initServices() {
    var section = $('.services-section');
    if (!section || !hasGSAP() || !animate) return;
    var gsap = window.gsap;
    var titlesContainer = $('.services-titles', section);
    var titlesWrap = $('.services-titles-container', section);
    var imagesContainer = $('.services-images', section);
    var header = $('.services-header', section);
    var intro = $$('.services-intro-text', section);
    var bgWrap = $('.services-bg-img', section);
    var bgInner = $('.bg', bgWrap);
    var bgPhoto = $('.bg-photo', bgWrap);
    var bgPh = $('.bg-ph', bgWrap);
    var titles = $$('p', titlesContainer);
    var imgs = $$('.services-img', imagesContainer);
    var n = titles.length;
    if (!n) return;

    // serve a photo sized for this screen instead of the largest rendition
    function fit(u) {
      if (!u) return u;
      var w = Math.min(2400, Math.ceil(window.innerWidth * Math.min(window.devicePixelRatio || 1, 2) / 100) * 100);
      return u.replace(/([?&])width=\d+/, '$1width=' + w);
    }
    function setBg(i) {
      var t = titles[i], url = fit(t.getAttribute('data-bg'));
      if (url) { bgPhoto.src = url; bgPhoto.hidden = false; bgPh.hidden = true; }
      else { bgPhoto.hidden = true; bgPh.hidden = false; bgPh.className = 'ph ph' + (t.getAttribute('data-ph') || 2) + ' bg-ph'; }
    }
    setBg(0);
    // warm the cache so every collection photo is ready before its title becomes active
    (window.requestIdleCallback || function (fn) { setTimeout(fn, 600); })(function () {
      titles.forEach(function (t) { var u = fit(t.getAttribute('data-bg')); if (u) { var im = new Image(); im.decoding = 'async'; im.src = u; } });
    });

    var cfg = { gap: Math.min(0.12, 1 / (n * 2)), speed: 0.3 };
    var totalDur = (n - 1) * cfg.gap + cfg.speed;
    var scrollH = Math.max(window.innerHeight * (2 + n * 0.8), window.innerHeight * 0.2);
    var active = 0;

    // desktop: arc bulges to the right of the list; mobile: thumbnails ride the right edge
    function geo() {
      var W = window.innerWidth, H = window.innerHeight, m = W < 768;
      var sx = m ? W * 0.84 : W * 0.3 - 220;
      return { m: m, W: W, H: H, sx: sx, sy: -200, ey: H + 200, cx: m ? sx - 40 : sx + 500, cy: H / 2 };
    }
    function bez(t, g) {
      return {
        x: (1 - t) * (1 - t) * g.sx + 2 * (1 - t) * t * g.cx + t * t * g.sx,
        y: (1 - t) * (1 - t) * g.sy + 2 * (1 - t) * t * g.cy + t * t * g.ey
      };
    }
    function imgProg(i, p) {
      var s = i * cfg.gap, e = s + cfg.speed;
      if (p < s) return -1;
      if (p > e) return 2;
      return (p - s) / cfg.speed;
    }
    function lines(on) { gsap.set(titlesWrap, { '--services-before-opacity': on, '--services-after-opacity': on }); }

    var st = window.ScrollTrigger.create({
      trigger: section, start: 'top top', end: '+=' + scrollH + 'px', pin: true, pinSpacing: true, scrub: 1, invalidateOnRefresh: true,
      onUpdate: function (self) {
        var p = self.progress, g = geo();
        if (p <= 0.2) {
          var a = p / 0.2, d = g.W * (g.m ? 0.4 : 0.6);
          gsap.set(intro[0], { x: -a * d, opacity: 1 });
          gsap.set(intro[1], { x: a * d, opacity: 1 });
          gsap.set(bgWrap, { transform: 'scale(' + a + ')' });
          gsap.set(bgInner, { transform: 'scale(' + (1.5 - a * 0.5) + ')' });
          imgs.forEach(function (im) { gsap.set(im, { opacity: 0 }); });
          header.style.opacity = '0'; lines('0');
        } else if (p <= 0.25) {
          gsap.set(bgWrap, { transform: 'scale(1)' }); gsap.set(bgInner, { transform: 'scale(1)' });
          intro.forEach(function (el) { gsap.set(el, { opacity: 0 }); });
          imgs.forEach(function (im) { gsap.set(im, { opacity: 0 }); });
          header.style.opacity = '1'; lines('1');
        } else if (p <= 0.95) {
          gsap.set(bgWrap, { transform: 'scale(1)' }); gsap.set(bgInner, { transform: 'scale(1)' });
          intro.forEach(function (el) { gsap.set(el, { opacity: 0 }); });
          header.style.opacity = '1'; lines('1');
          var sp = (p - 0.25) / 0.7, np = sp * totalDur;
          var th = titlesContainer.scrollHeight;
          gsap.set(titlesContainer, { transform: 'translateY(' + (g.H - sp * (g.H + th)) + 'px)' });
          imgs.forEach(function (im, i) {
            var ip = imgProg(i, np);
            if (ip < 0 || ip > 1) gsap.set(im, { opacity: 0 });
            else { var pos = bez(ip, g); gsap.set(im, { x: pos.x - (g.m ? 40 : 100), y: pos.y - (g.m ? 30 : 75), opacity: 1 }); }
          });
          var mid = g.H / 2, ci = 0, cd = Infinity;
          titles.forEach(function (t, i) {
            var r = t.getBoundingClientRect(), dist = Math.abs(r.top + r.height / 2 - mid);
            if (dist < cd) { cd = dist; ci = i; }
          });
          if (ci !== active) {
            titles[active].style.opacity = '0.25'; titles[ci].style.opacity = '1';
            setBg(ci); active = ci;
          }
        } else { header.style.opacity = '0'; lines('0'); }
      }
    });
    cleanups.push(function () { st.kill(true); });
  }

  /* =======================================================================
     Shrinking image (pinned)
     ======================================================================= */
  function initExperience() {
    var section = $('#scroll-experience');
    var image = $('[data-exp-image]');
    if (!section || !image || !hasGSAP() || !animate) return;
    var m = window.gsap.matchMedia();
    var pin = function (prop, to) {
      window.gsap.set(image, { width: '100%', height: '100%' });
      window.ScrollTrigger.create({
        trigger: section, start: 'top top', end: function () { return '+=' + window.innerHeight * 2; },
        pin: true, pinSpacing: true, scrub: 1, invalidateOnRefresh: true,
        onUpdate: function (self) { var o = {}; o[prop] = (100 - self.progress * (100 - to)) + '%'; window.gsap.set(image, o); }
      });
    };
    m.add('(min-width: 769px)', function () { pin('width', 50); });    // photo slides sideways
    m.add('(max-width: 768px)', function () { pin('height', 44); });   // photo drops down, copy revealed above
    mm.push(m);
  }

  /* =======================================================================
     Testimonials story — marquee → Flip → horizontal panels
     ======================================================================= */
  function initTestimonials() {
    var container = $('.testimonial-container');
    var wrapper = $('.testimonial-horizontal-scroll-wrapper');
    var strip = $('#marqueeStrip');
    if (!container || !wrapper || !strip || !hasGSAP() || !window.Flip || !animate) return;
    var gsap = window.gsap, ST = window.ScrollTrigger;

    var cs = getComputedStyle(container);
    var light = cs.getPropertyValue('--color-bg').trim() || '#ffffff';
    var tint = cs.getPropertyValue('--color-surface').trim() || light;
    var slides = $$('.testimonial-horizontal-slide', wrapper);
    var total = slides.length, content = total - 1;
    if (content < 1) return;
    wrapper.style.width = (total * 100) + 'vw';

    gsap.set(strip, { xPercent: -75, yPercent: -50 });
    ST.create({
      trigger: '.testimonial-marquee', start: 'top bottom', end: 'top top', scrub: true,
      onUpdate: function (self) { gsap.set(strip, { xPercent: -75 + self.progress * 25, yPercent: -50 }); }
    });

    var clone = null, active = false, flip = null;
    function pinEl() { return $('.testimonial-marquee-img.testimonial-pin'); }
    function createClone() {
      if (active) return;
      var orig = pinEl();
      if (!orig) return;
      var rect = orig.getBoundingClientRect();
      clone = orig.cloneNode(true);
      clone.classList.remove('testimonial-marquee-img');
      clone.classList.add('testimonial-clone');
      gsap.set(clone, {
        position: 'fixed',
        left: rect.left + rect.width / 2 - orig.offsetWidth / 2 + 'px',
        top: rect.top + rect.height / 2 - orig.offsetHeight / 2 + 'px',
        width: orig.offsetWidth + 'px', height: orig.offsetHeight + 'px',
        transform: 'rotate(-5deg)', transformOrigin: 'center center',
        pointerEvents: 'none', zIndex: 1000
      });
      // the clone ends up full-screen: use a full-screen rendition, not the small tile one
      var cimg = $('img', clone);
      if (cimg) { cimg.sizes = '100vw'; cimg.loading = 'eager'; cimg.decoding = 'sync'; }
      doc.body.appendChild(clone);
      gsap.set(orig, { opacity: 0 });
      active = true;
    }
    function removeClone() {
      if (!active) return;
      if (clone) clone.remove();
      clone = null;
      var orig = pinEl();
      if (orig) gsap.set(orig, { opacity: 1 });
      active = false;
    }
    cleanups.push(function () { if (flip) { flip.kill(); flip = null; } removeClone(); });

    ST.create({ trigger: '.testimonial-horizontal-scroll', start: 'top top', end: function () { return '+=' + window.innerHeight * 5; }, pin: true });
    ST.create({ trigger: '.testimonial-marquee', start: 'top top', onEnter: createClone, onEnterBack: createClone, onLeaveBack: removeClone });

    ST.create({
      trigger: '.testimonial-horizontal-scroll', start: 'top 50%', end: function () { return '+=' + window.innerHeight * 5.5; },
      onEnter: function () {
        if (clone && !flip) {
          var state = window.Flip.getState(clone);
          gsap.set(clone, { position: 'fixed', left: '0px', top: '0px', width: '100%', height: window.innerHeight + 'px', transform: 'rotate(0deg)', zIndex: 1000 });
          flip = window.Flip.from(state, { duration: 1, ease: 'none', paused: true });
        }
      },
      onLeaveBack: function () {
        if (flip) { flip.kill(); flip = null; }
        gsap.set(container, { backgroundColor: light });
        gsap.set(wrapper, { x: '0%' });
      },
      onUpdate: function (self) {
        var p = self.progress;
        if (p <= 0.05) gsap.set(container, { backgroundColor: gsap.utils.interpolate(light, tint, Math.min(p / 0.05, 1)) });
        else gsap.set(container, { backgroundColor: tint });
        if (p <= 0.2 && flip) flip.progress(p / 0.2);
        var max = (content / total) * 100;
        if (p > 0.2 && p <= 0.95) {
          if (flip) flip.progress(1);
          var h = (p - 0.2) / 0.75;
          gsap.set(wrapper, { x: (-max * h) + '%' });
          if (clone) gsap.set(clone, { x: (-content * 100 * h) + '%' });
        }
        if (p > 0.95) {
          if (flip) flip.progress(1);
          if (clone) gsap.set(clone, { x: (-content * 100) + '%' });
          gsap.set(wrapper, { x: (-max) + '%' });
        }
      }
    });
  }

  /* =======================================================================
     Zoom-reveal banner
     ======================================================================= */
  function initCTA() {
    var banner = $('.cta-banner');
    var container = $('.cta-banner-img-container');
    if (!banner || !container || !hasGSAP() || !animate) return;
    var gsap = window.gsap;
    var intro = $$('.cta-banner-intro-text', banner);
    var masks = $$('.cta-mask', banner);
    var h3 = $('.cta-banner-header h3', banner);
    var split = h3 && window.SplitText ? new window.SplitText(h3, { type: 'words' }) : null;
    var words = split ? split.words : [];
    gsap.set(words, { opacity: 0 });
    masks.forEach(function (l, i) { gsap.set(l, { scale: 0.9 - i * 0.2 }); });
    gsap.set(container, { scale: 0 });

    var st = window.ScrollTrigger.create({
      trigger: banner, start: 'top top', end: function () { return '+=' + window.innerHeight; }, pin: true, pinSpacing: true, scrub: 1, invalidateOnRefresh: true,
      onUpdate: function (self) {
        var p = self.progress;
        gsap.set(container, { scale: p });
        masks.forEach(function (l, i) {
          var init = 0.9 - i * 0.2;
          gsap.set(l, { scale: init + Math.min(p / 0.9, 1) * (1 - init) });
        });
        if (p <= 0.9) {
          var d = window.innerWidth * 0.5;
          if (intro[0]) gsap.set(intro[0], { x: -(p / 0.9) * d });
          if (intro[1]) gsap.set(intro[1], { x: (p / 0.9) * d });
        }
        if (words.length) {
          if (p >= 0.7 && p <= 0.9) {
            var hp = (p - 0.7) / 0.2;
            words.forEach(function (w, i) {
              var s = i / words.length, e = (i + 1) / words.length;
              gsap.set(w, { opacity: hp >= e ? 1 : hp >= s ? (hp - s) / (e - s) : 0 });
            });
          } else if (p < 0.7) gsap.set(words, { opacity: 0 });
          else gsap.set(words, { opacity: 1 });
        }
      }
    });
    cleanups.push(function () { st.kill(true); if (split) split.revert(); });
  }

  /* =======================================================================
     FAQ accordion
     ======================================================================= */
  function initFAQ() {
    $$('.faq').forEach(function (f, i) {
      if (f.__luxFaq) return;
      f.__luxFaq = true;
      var q = $('.faq-q', f), a = $('.faq-a', f);
      if (!q || !a) return;
      q.setAttribute('role', 'button'); q.setAttribute('tabindex', '0'); q.setAttribute('aria-expanded', 'false');
      if (!a.id) a.id = 'faq-a-' + i + '-' + Math.random().toString(36).slice(2, 6);
      q.setAttribute('aria-controls', a.id);
      var toggle = function () {
        var open = f.classList.toggle('open');
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (hasGSAP()) window.gsap.to(a, { height: open ? 'auto' : 0, duration: 0.3, ease: 'power1.inOut', onComplete: function () { window.ScrollTrigger.refresh(); } });
        else a.style.height = open ? 'auto' : '0';
      };
      f.addEventListener('click', function (e) { if (e.target.closest('.faq-a')) return; toggle(); });
      q.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
    });
  }

  /* =======================================================================
     Navigation: mobile menu, floating nav, progress bar, anchors
     ======================================================================= */
  var navBound = false;
  function initNav() {
    var toggle = $('[data-nav-toggle]');
    var menu = $('#MobileMenu');
    if (toggle && menu && !toggle.__luxBound) {
      toggle.__luxBound = true;
      var setOpen = function (open) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu.hidden = !open;
        doc.body.classList.toggle('menu-open', open);
        lockScroll(open);
      };
      toggle.addEventListener('click', function () { setOpen(toggle.getAttribute('aria-expanded') !== 'true'); });
      doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !menu.hidden) setOpen(false); });
      menu.addEventListener('click', function (e) { if (e.target.closest('a')) setOpen(false); });
      window.addEventListener('resize', function () { if (window.innerWidth > 1000 && !menu.hidden) setOpen(false); });
    }
    if (navBound) return;
    navBound = true;

    var bar = $('#progress'), last = 0;
    window.addEventListener('scroll', function () {
      var nf = $('#navFloat');
      var y = window.scrollY, max = root.scrollHeight - window.innerHeight;
      if (bar) bar.style.transform = 'scaleX(' + (max > 0 ? y / max : 0) + ')';
      if (nf && LUX.floatingNav !== false) {
        var up = y < last - 4, down = y > last + 4;
        if (y > window.innerHeight * 1.2 && up) { nf.classList.add('show'); nf.setAttribute('aria-hidden', 'false'); $$('a', nf).forEach(function (a) { a.tabIndex = 0; }); }
        else if (down || y < window.innerHeight * 0.8) { nf.classList.remove('show'); nf.setAttribute('aria-hidden', 'true'); $$('a', nf).forEach(function (a) { a.tabIndex = -1; }); }
        last = y;
      }
    }, { passive: true });

    doc.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id.length < 2 || a.hasAttribute('data-recover-toggle')) return;
      var t = null;
      try { t = $(id); } catch (err) { return; }
      if (!t) return;
      e.preventDefault();
      scrollToEl(t);
    });
  }

  /* =======================================================================
     Announcement bar
     ======================================================================= */
  function initAnnouncement() {
    $$('[data-announcement]').forEach(function (bar) {
      if (bar.__luxAnn) return;
      bar.__luxAnn = true;
      var slides = $$('.announcement__slide', bar);
      if (slides.length < 2) return;
      var i = 0, timer = null;
      var show = function (n) {
        slides[i].classList.remove('is-active');
        i = (n + slides.length) % slides.length;
        slides[i].classList.add('is-active');
      };
      var start = function () {
        if (bar.getAttribute('data-autoplay') !== 'true' || reduced) return;
        clearInterval(timer);
        timer = setInterval(function () { show(i + 1); }, parseInt(bar.getAttribute('data-speed'), 10) || 5000);
      };
      var prev = $('[data-announcement-prev]', bar), next = $('[data-announcement-next]', bar);
      if (prev) prev.addEventListener('click', function () { show(i - 1); start(); });
      if (next) next.addEventListener('click', function () { show(i + 1); start(); });
      bar.addEventListener('mouseenter', function () { clearInterval(timer); });
      bar.addEventListener('mouseleave', start);
      start();
    });
  }

  /* =======================================================================
     Cart drawer + ajax cart
     ======================================================================= */
  var cartBound = false;
  function drawerEl() { return $('[data-cart-drawer]'); }
  function overlayEl() { return $('[data-cart-overlay]'); }
  var drawerOpen = false;

  function openCart() {
    var d = drawerEl(), o = overlayEl();
    if (!d) return false;
    drawerOpen = true;
    d.classList.add('is-open'); if (o) o.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    setTimeout(function () { d.focus(); }, 50);
    return true;
  }
  function closeCart() {
    var d = drawerEl(), o = overlayEl();
    drawerOpen = false;
    if (d) { d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); }
    if (o) o.classList.remove('is-open');
    lockScroll(false);
  }
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (el) { el.textContent = count; el.classList.toggle('is-empty', !count); });
  }
  function renderCartSections(sections) {
    if (!sections || !sections['cart-drawer']) return;
    var wrap = $('#shopify-section-cart-drawer');
    if (!wrap) return;
    wrap.innerHTML = sections['cart-drawer'];
    if (drawerOpen) {
      var d = drawerEl(), o = overlayEl();
      if (d) { d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); }
      if (o) o.classList.add('is-open');
    }
  }
  function refreshCart() {
    return Promise.all([
      fetch((R.cart || '/cart') + '?sections=cart-drawer').then(function (r) { return r.json(); }),
      fetch((R.root || '/') + 'cart.js').then(function (r) { return r.json(); })
    ]).then(function (res) {
      renderCartSections(res[0]);
      updateCartCount(res[1].item_count);
      return res[1];
    });
  }
  function changeLine(line, quantity) {
    return fetch(R.cartChange || '/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
    }).then(function (r) { return r.json(); }).then(function (cart) {
      renderCartSections(cart.sections);
      updateCartCount(cart.item_count);
    });
  }
  function initCart() {
    if (cartBound) return;
    cartBound = true;
    doc.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-cart-open]')) { e.preventDefault(); if (!openCart()) window.location.href = R.cart || '/cart'; return; }
      if (t.closest('[data-cart-close]') || t.closest('[data-cart-overlay]')) { closeCart(); return; }
      var q = t.closest('[data-qty-change]');
      if (q) {
        var line = q.closest('[data-line]');
        if (line) { e.preventDefault(); changeLine(parseInt(line.getAttribute('data-line'), 10), parseInt(q.getAttribute('data-qty-change'), 10)); }
      }
    });
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawerOpen) closeCart(); });
  }

  /* =======================================================================
     Predictive search
     ======================================================================= */
  var searchBound = false;
  function initSearch() {
    if (searchBound) return;
    var modal = $('[data-search-modal]');
    if (!modal) return;
    searchBound = true;
    var overlay = $('[data-search-overlay]');
    var input = $('[data-search-input]', modal);
    var results = $('[data-predictive-results]', modal);
    var ctrl = null;

    function open() {
      modal.classList.add('is-open'); if (overlay) overlay.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false'); lockScroll(true);
      setTimeout(function () { if (input) input.focus(); }, 80);
    }
    function close() {
      modal.classList.remove('is-open'); if (overlay) overlay.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true'); lockScroll(false);
    }
    doc.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-open]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-search-close]') || e.target.closest('[data-search-overlay]')) close();
    });
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && modal.classList.contains('is-open')) close(); });

    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      if (ctrl) ctrl.abort();
      ctrl = window.AbortController ? new AbortController() : null;
      var url = (R.predictiveSearch || '/search/suggest') + '?q=' + encodeURIComponent(q) +
        '&section_id=predictive-search&resources[type]=product,collection,article,page,query&resources[limit]=6';
      fetch(url, ctrl ? { signal: ctrl.signal } : {}).then(function (r) { return r.text(); }).then(function (html) {
        var tmp = doc.createElement('div'); tmp.innerHTML = html;
        var sec = $('#shopify-section-predictive-search', tmp) || tmp;
        results.innerHTML = sec.innerHTML;
      }).catch(function () {});
    }, 250);
    if (input) input.addEventListener('input', run);
  }

  /* =======================================================================
     Collection / search filters
     ======================================================================= */
  function initFilters() {
    var form = $('[data-filters-form]');
    var panel = $('[data-filters-panel]');
    var overlay = $('[data-filters-overlay]');
    if (form && !form.__luxFilters) {
      form.__luxFilters = true;
      var submit = debounce(function () { form.submit(); }, 350);
      form.addEventListener('change', submit);
    }
    if (panel && !panel.__luxPanel) {
      panel.__luxPanel = true;
      var set = function (open) { panel.classList.toggle('is-open', open); if (overlay) overlay.classList.toggle('is-open', open); lockScroll(open && window.innerWidth <= 1000); };
      doc.addEventListener('click', function (e) {
        if (e.target.closest('[data-filters-open]')) set(true);
        else if (e.target.closest('[data-filters-close]') || e.target.closest('[data-filters-overlay]')) set(false);
      });
    }
    $$('[data-sort-select]').forEach(function (sel) {
      if (sel.__luxSort) return;
      sel.__luxSort = true;
      sel.addEventListener('change', function () { sel.form.submit(); });
    });
  }

  /* =======================================================================
     Product page: variants (Section Rendering API), gallery, lightbox, add to cart
     ======================================================================= */
  function initProduct() {
    var section = $('[data-section-type="product"]');
    if (!section || section.__luxProduct) return;
    section.__luxProduct = true;
    var sid = section.getAttribute('data-section-id');
    var pUrl = section.getAttribute('data-product-url');
    var variants = [];

    function readVariants() {
      var el = $('[data-variants]', section);
      try { variants = el ? JSON.parse(el.textContent) : []; } catch (e) { variants = []; }
    }
    readVariants();

    function activateMedia(id) {
      if (!id) return;
      $$('.product-gallery__item', section).forEach(function (it) { it.classList.toggle('is-active', it.getAttribute('data-media-id') === String(id)); });
      $$('.thumb-btn', section).forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-thumb') === String(id)); });
    }
    function selectedVariant() {
      var opts = $$('.option-group', section).map(function (g) {
        var c = $('input:checked', g) || $('select', g);
        return c ? c.value : null;
      });
      if (!opts.length) return variants[0];
      return variants.find(function (v) { return opts.every(function (o, i) { return v['option' + (i + 1)] === o; }); });
    }
    function swapInfo(v) {
      var info = $('[data-product-info]', section);
      var qty = $('input[name="quantity"]', info), qv = qty ? qty.value : null;
      info.classList.add('is-loading');
      fetch(pUrl + '?variant=' + v.id + '&section_id=' + sid)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmp = doc.createElement('div'); tmp.innerHTML = html;
          var fresh = $('[data-product-info]', tmp);
          if (fresh) {
            info.innerHTML = fresh.innerHTML;
            readVariants();
            var q2 = $('input[name="quantity"]', info);
            if (q2 && qv) q2.value = qv;
          }
          if (v.featured_media) activateMedia(v.featured_media.id);
          if (window.history && window.history.replaceState) {
            var u = new URL(window.location.href); u.searchParams.set('variant', v.id);
            window.history.replaceState({}, '', u.toString());
          }
        })
        .catch(function () {})
        .then(function () { info.classList.remove('is-loading'); });
    }
    section.addEventListener('change', function (e) {
      if (!e.target.closest('.option-group')) return;
      var v = selectedVariant();
      if (v) swapInfo(v);
      else { var b = $('[data-add-to-cart]', section); if (b) { b.disabled = true; } }
    });

    section.addEventListener('click', function (e) {
      var t = e.target;
      var thumb = t.closest('.thumb-btn');
      if (thumb) { activateMedia(thumb.getAttribute('data-thumb')); return; }
      var minus = t.closest('[data-qty-minus]'), plus = t.closest('[data-qty-plus]');
      if (minus || plus) {
        var qty = $('input[name="quantity"]', section);
        var step = parseInt(qty.step, 10) || 1, min = parseInt(qty.min, 10) || 1;
        var cur = parseInt(qty.value, 10) || min;
        qty.value = Math.max(min, plus ? cur + step : cur - step);
        return;
      }
      var z = t.closest('[data-zoomable]');
      if (z && section.getAttribute('data-zoom') === 'true') openLightbox(z);
    });

    section.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-product-form]');
      if (!form || !window.fetch || !window.FormData) return;
      e.preventDefault();
      var btn = $('[data-add-to-cart]', form), msg = $('[data-form-message]', form);
      if (btn) btn.disabled = true;
      var body = new FormData(form);
      if (LUX.cartType === 'drawer') { body.append('sections', 'cart-drawer'); body.append('sections_url', window.location.pathname); }
      fetch((R.cartAdd || '/cart/add.js'), { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: body })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.json.description || res.json.message || S.error);
          return fetch((R.root || '/') + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            updateCartCount(cart.item_count);
            if (LUX.cartType === 'drawer' && res.json.sections) {
              renderCartSections(res.json.sections);
              if (LUX.cartOpenOnAdd !== false) openCart();
              if (msg) msg.innerHTML = S.added || '';
            } else if (msg) {
              msg.innerHTML = (S.added || 'Added') + ' · <a href="' + (R.cart || '/cart') + '">' + (S.viewCart || 'View cart') + '</a>';
            }
          });
        })
        .catch(function (err) { if (msg) msg.textContent = err.message || S.error || ''; })
        .then(function () { var v = selectedVariant(); if (btn) btn.disabled = !(v && v.available); });
    });
  }

  function openLightbox(item) {
    var lb = $('[data-lightbox]'); if (!lb) return;
    var img = $('img', item); if (!img) return;
    var big = img.getAttribute('data-full') || img.currentSrc || img.src;
    $('[data-lightbox-img]', lb).src = big;
    lb.hidden = false; lockScroll(true);
    var close = function () { lb.hidden = true; lockScroll(false); };
    lb.onclick = function (e) { if (e.target === lb || e.target.closest('[data-lightbox-close]')) close(); };
    doc.addEventListener('keydown', function esc(e) { if (e.key === 'Escape') { close(); doc.removeEventListener('keydown', esc); } });
  }

  function initRecommendations() {
    $$('.product-recommendations[data-url]').forEach(function (el) {
      if (el.__luxRec) return;
      el.__luxRec = true;
      fetch(el.getAttribute('data-url')).then(function (r) { return r.text(); }).then(function (html) {
        var tmp = doc.createElement('div'); tmp.innerHTML = html;
        var fresh = $('.product-recommendations', tmp);
        if (fresh && fresh.innerHTML.trim().length) { el.innerHTML = fresh.innerHTML; initFadeUps(); if (hasGSAP()) window.ScrollTrigger.refresh(); }
      }).catch(function () {});
    });
  }

  /* =======================================================================
     Small helpers: video, localization, account addresses, login toggle
     ======================================================================= */
  function initMisc() {
    $$('[data-video-play]').forEach(function (btn) {
      if (btn.__luxVid) return;
      btn.__luxVid = true;
      btn.addEventListener('click', function () {
        var type = btn.getAttribute('data-video-type'), id = btn.getAttribute('data-video-id');
        var frame = btn.closest('[data-video-frame]');
        var src = type === 'vimeo' ? 'https://player.vimeo.com/video/' + id + '?autoplay=1' : 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
        var iframe = doc.createElement('iframe');
        iframe.src = src; iframe.allow = 'autoplay; fullscreen; picture-in-picture'; iframe.allowFullscreen = true; iframe.title = 'Video';
        frame.innerHTML = ''; frame.appendChild(iframe);
      });
    });

    $$('[data-localization-select]').forEach(function (sel) {
      if (sel.__luxLoc) return;
      sel.__luxLoc = true;
      sel.addEventListener('change', function () { sel.form.submit(); });
    });

    $$('select[data-default]').forEach(function (sel) {
      var d = sel.getAttribute('data-default');
      if (d) { Array.prototype.some.call(sel.options, function (o) { if (o.text === d || o.value === d) { sel.value = o.value; return true; } return false; }); }
    });
    $$('[data-address-toggle]').forEach(function (btn) {
      if (btn.__luxAddr) return;
      btn.__luxAddr = true;
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-address-toggle');
        var box = $(key === 'new' ? '#AddressNew' : '#Address' + key);
        if (!box) return;
        box.hidden = !box.hidden;
        btn.setAttribute('aria-expanded', box.hidden ? 'false' : 'true');
      });
    });
    $$('[data-address-cancel]').forEach(function (btn) {
      btn.addEventListener('click', function () { var box = btn.closest('.address-editor'); if (box) box.hidden = true; });
    });
    $$('[data-address-delete]').forEach(function (f) {
      f.addEventListener('submit', function (e) { if (!window.confirm(S.confirmDelete || 'Delete this address?')) e.preventDefault(); });
    });

    var login = $('#CustomerLoginForm'), recover = $('#RecoverPasswordForm');
    if (login && recover) {
      $$('[data-recover-toggle]').forEach(function (a) {
        a.addEventListener('click', function (e) { e.preventDefault(); login.hidden = !login.hidden; recover.hidden = !recover.hidden; });
      });
      if (window.location.hash === '#recover') { login.hidden = true; recover.hidden = false; }
    }
  }

  /* =======================================================================
     Enhancements: stats count-up, magnetic buttons, parallax
     ======================================================================= */
  function initStats() {
    if (!hasGSAP() || !animate) return;
    $$('[data-count]').forEach(function (el) {
      if (el.__luxCount) return;
      el.__luxCount = true;
      var end = parseFloat(el.getAttribute('data-count'));
      var suffix = el.textContent.replace(/[0-9.,\s]/g, '');
      var o = { v: 0 };
      el.textContent = '0' + suffix;
      window.ScrollTrigger.create({
        trigger: el, start: 'top 90%', once: true,
        onEnter: function () { window.gsap.to(o, { v: end, duration: 1.6, ease: 'power2.out', delay: 0.4, onUpdate: function () { el.textContent = Math.round(o.v) + suffix; } }); }
      });
    });
  }
  function initMagnetic() {
    if (!LUX.magnetic || !hasGSAP() || reduced || !window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    $$('.btn').forEach(function (b) {
      if (b.__luxMag || b.closest('.drawer') || b.closest('form')) return;
      b.__luxMag = true;
      var xTo = window.gsap.quickTo(b, 'x', { duration: 0.4, ease: 'power3' });
      var yTo = window.gsap.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('mousemove', function (e) { var r = b.getBoundingClientRect(); xTo((e.clientX - r.left - r.width / 2) * 0.18); yTo((e.clientY - r.top - r.height / 2) * 0.28); });
      b.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
    });
  }
  function initParallax() {
    if (!hasGSAP() || !animate) return;
    $$('.bento-card--image .bento-card__bg, .blog-feature .thumb').forEach(function (box) {
      var target = $('img, .ph', box);
      if (!target || target.__luxPar) return;
      target.__luxPar = true;
      window.gsap.fromTo(target, { yPercent: -6, scale: 1.14 }, {
        yPercent: 6, scale: 1.14, ease: 'none',
        scrollTrigger: { trigger: box, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* =======================================================================
     Boot / re-boot (theme editor)
     ======================================================================= */
  function afterPreloader() { heroLines.splice(0).forEach(function (f) { f(); }); }

  function initCommerce() {
    initNav(); initAnnouncement(); initCart(); initSearch(); initFilters(); initProduct(); initRecommendations(); initFAQ(); initMisc();
  }
  function initAll() {
    initCommerce();
    if (hasGSAP()) {
      window.gsap.registerPlugin.apply(window.gsap, [window.ScrollTrigger, window.SplitText, window.Flip, window.CustomEase].filter(Boolean));
      initServices(); initExperience(); initTestimonials(); initCTA();   // pinned sections first
      initTextAnimations(); initFadeUps(); initStats(); initParallax(); initMagnetic();
      window.ScrollTrigger.refresh();
    }
  }
  function teardown() {
    if (!hasGSAP()) return;
    cleanups.splice(0).forEach(function (fn) { try { fn(); } catch (e) {} });
    mm.splice(0).forEach(function (m) { try { m.revert(); } catch (e) {} });
    textRecs.splice(0).forEach(function (r) { try { r.split.revert(); } catch (e) {} if (r.el) r.el.__luxSplit = null; });
    window.ScrollTrigger.getAll().forEach(function (t) { t.kill(true); });
    $$('.fade-up').forEach(function (el) { el.__luxFade = false; window.gsap.set(el, { clearProps: 'all' }); });
    $$('[data-count]').forEach(function (el) { el.__luxCount = false; });
    $$('.btn').forEach(function (el) { el.__luxMag = false; window.gsap.set(el, { clearProps: 'x,y' }); });
    $$('.bento-card__bg img, .bento-card__bg .ph, .blog-feature .thumb img, .blog-feature .thumb .ph').forEach(function (el) { el.__luxPar = false; });
  }

  // Stable viewport height (mobile browser bars) + touch-scroll normalisation so scrubbed
  // animations behave on phones exactly as they do with a mouse wheel on desktop.
  function setVH() { root.style.setProperty('--vh', (window.innerHeight * 0.01) + 'px'); }
  setVH();
  window.addEventListener('orientationchange', function () { setTimeout(function () { setVH(); if (hasGSAP()) window.ScrollTrigger.refresh(); }, 300); });
  window.addEventListener('resize', debounce(function () { if (!(window.ScrollTrigger && window.ScrollTrigger.isTouch)) setVH(); }, 150));
  function setupTouchScroll() {
    if (!hasGSAP() || reduced) return;
    window.gsap.registerPlugin(window.ScrollTrigger);
    window.ScrollTrigger.config({ ignoreMobileResize: true });
    if (window.ScrollTrigger.isTouch && !window.__luxNormalized) {
      window.__luxNormalized = true;
      window.ScrollTrigger.normalizeScroll({ allowNestedScroll: true });
    }
  }

  function boot() {
    if (booted) return;
    booted = true;
    setupTouchScroll();
    initSmoothScroll();
    var fontsReady = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();
    var go = function () { initAll(); afterPreloader(); };
    if (LUX.preloader && $('.hero')) {
      initCommerce();
      fontsReady.then(function () {
        window.gsap.registerPlugin.apply(window.gsap, [window.ScrollTrigger, window.SplitText, window.Flip, window.CustomEase].filter(Boolean));
        initServices(); initExperience(); initTestimonials(); initCTA();
        initTextAnimations(); initFadeUps(); initStats(); initParallax(); initMagnetic();
        window.ScrollTrigger.refresh();
      });
      initPreloader(afterPreloader);
    } else {
      root.classList.remove('lux-preloader');
      fontsReady.then(go);
    }
    setTimeout(function () { if (hasGSAP()) window.ScrollTrigger.refresh(); }, 150);
    setTimeout(function () { if (hasGSAP()) window.ScrollTrigger.refresh(); }, 600);
    window.addEventListener('load', function () { if (hasGSAP()) window.ScrollTrigger.refresh(); });
  }

  var editorT;
  function editorRefresh() {
    clearTimeout(editorT);
    editorT = setTimeout(function () { teardown(); initAll(); afterPreloader(); }, 200);
  }
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (evt) { doc.addEventListener(evt, editorRefresh); });
  doc.addEventListener('shopify:section:select', function (e) {
    // open the cart drawer / search while editing those sections
    if (e.target && e.target.id === 'shopify-section-cart-drawer') openCart();
  });
  doc.addEventListener('shopify:section:deselect', function (e) {
    if (e.target && e.target.id === 'shopify-section-cart-drawer') closeCart();
  });

  function start() {
    if (!hasGSAP() && LUX.animations !== false) {
      root.classList.remove('lux-preloader');
      initCommerce();
      return;
    }
    boot();
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})();
